// Sticky banner that appears on every non-fullscreen page while a run is
// active in the background. Taps navigate the user back to /run/live so
// they can resume + finish.
//
// Polls localStorage every 3 seconds so we notice a session started in
// another tab / component without prop drilling.

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Footprints, Radio } from 'lucide-react';
import { loadRunSession, elapsedSec } from '../../lib/run-session';
import { formatDuration } from '../../lib/formatters';

export function ActiveRunBanner() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [session, setSession] = useState(() => loadRunSession());
  const [, tick] = useState(0);

  // Poll for session presence / updates.
  useEffect(() => {
    const iv = setInterval(() => {
      setSession(loadRunSession());
      tick((n) => n + 1);
    }, 3000);
    return () => clearInterval(iv);
  }, []);

  // Also re-check on window focus (user returning from another tab/app).
  useEffect(() => {
    const onVis = () => setSession(loadRunSession());
    window.addEventListener('focus', onVis);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onVis);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Never show ON the live-run screens themselves.
  const hideOn = ['/run/live'];
  if (!session) return null;
  if (hideOn.includes(pathname)) return null;
  if (pathname.match(/^\/events\/[^/]+\/race$/)) return null;

  const elapsed = elapsedSec(session);
  const distanceKm = (session.distanceMeters || 0) / 1000;
  const isPaused = session.pausedAt != null;
  const isRace = session.type === 'race';

  const target = isRace && session.eventId ? `/events/${session.eventId}/race` : '/run/live';

  return (
    <button
      onClick={() => nav(target)}
      data-testid="active-run-banner"
      className="sticky top-0 z-40 w-full bg-primary text-primary-foreground border-b border-primary/40 shadow-md text-left"
    >
      <div className="mx-auto max-w-md px-4 py-2.5 flex items-center gap-3">
        <div className={`h-9 w-9 rounded-md bg-white/20 flex items-center justify-center ${isPaused ? '' : 'animate-pulse'}`}>
          {isRace ? <Radio className="h-4 w-4" /> : <Footprints className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-semibold opacity-90">
            {isPaused ? 'Paused' : 'Active'} · {isRace ? (session.eventName || 'Race') : 'Personal run'}
          </div>
          <div className="font-mono text-sm tabular-nums mt-0.5 truncate">
            {formatDuration(elapsed)} · {distanceKm.toFixed(2)} km · Tap to resume
          </div>
        </div>
        <div className="h-9 w-9 rounded-md bg-white/20 flex items-center justify-center">
          <Play className="h-4 w-4" />
        </div>
      </div>
    </button>
  );
}
